import axios from "axios";

// We deliberately do NOT route WhatsApp calls through the shared
// _apihelper getApi/postApi. That wrapper treats any 401/405/406 response
// as "session expired" and force-logs the user out — which is the right
// behavior for the admin/main backend but wrong here, because the
// WhatsApp service is an independent microservice. A 405 from the
// WhatsApp service (e.g. an unsupported preflight method on a specific
// route) should NOT log the agent out of the whole ATS. Using raw axios
// keeps WhatsApp failures contained.

const BASE = process.env.REACT_APP_WHATSAPP_BASEURL;

const newTransactionId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `txn_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 10)}`;
};

const baseHeaders = () => ({
  "Content-Type": "application/json",
  "x-transaction-id": newTransactionId(),
});

// Backend wraps every response in { success, data }. Pull `data` out.
const unwrap = (response) => response?.data?.data;

// Match the app-wide convention used in admin: surface backend message
// when present, fall back to a sensible default otherwise.
export const getErrorMessage = (error, fallback = "Something went wrong") =>
  error?.response?.data?.message || fallback;

const enc = (phone) => encodeURIComponent(phone);

// --- Phone numbers (used to derive the SSE channel's country name) ---

// GET /phone-numbers/integration/{integrationId}?skipCountry=true
// Returns the integration's registered WhatsApp business phones with their
// `country` column. The SSE channel is keyed by that country name (not the
// agent's selected country), so we read it from here.
export const fetchPhoneNumbers = async (integrationId) => {
  if (!integrationId) return [];
  const response = await axios.get(
    `${BASE}/phone-numbers/integration/${integrationId}`,
    { params: { skipCountry: true }, headers: baseHeaders(), withCredentials: true },
  );
  return unwrap(response) || [];
};

// --- Templates ---

// GET /templates/integration/{integrationId}
// Returns the full template list. Callers should filter to status === "APPROVED"
// before showing in the picker (Meta won't accept unapproved templates).
export const fetchTemplates = async (integrationId) => {
  if (!integrationId) return [];
  const response = await axios.get(
    `${BASE}/templates/integration/${integrationId}`,
    { headers: baseHeaders(), withCredentials: true },
  );
  return unwrap(response) || [];
};

// --- Conversations ---

// GET /conversations/integration/{id}/phone/{enc}/window
// → { open: bool, reason, lastInboundAt?, expiresAt?, remainingMs? }
export const fetchWindow = async (integrationId, phoneE164) => {
  const response = await axios.get(
    `${BASE}/conversations/integration/${integrationId}/phone/${enc(phoneE164)}/window`,
    { headers: baseHeaders(), withCredentials: true },
  );
  return unwrap(response) || { open: false, reason: "unknown" };
};

// GET /conversations/integration/{id}/phone/{enc}?limit=&offset=
// Returns the message thread, newest first per the Postman contract.
export const fetchMessages = async (
  integrationId,
  phoneE164,
  { limit = 50, offset = 0 } = {},
) => {
  const response = await axios.get(
    `${BASE}/conversations/integration/${integrationId}/phone/${enc(phoneE164)}`,
    { params: { limit, offset }, headers: baseHeaders(), withCredentials: true },
  );
  return unwrap(response) || [];
};

// POST /conversations/integration/{id}/phone/{enc}/read
// Clears unread_count for this contact. Call when the drawer opens.
export const markRead = async (integrationId, phoneE164) => {
  const response = await axios.post(
    `${BASE}/conversations/integration/${integrationId}/phone/${enc(phoneE164)}/read`,
    {},
    { headers: baseHeaders(), withCredentials: true },
  );
  return response?.data;
};

// POST /conversations/integration/{id}/phone/{enc}/send
// Free-text reply within the 24-hour window. Returns 400 with a useful
// `message` when the window is closed or the contact has opted out — let
// the caller surface that via getErrorMessage.
export const sendText = async (integrationId, phoneE164, text) => {
  const response = await axios.post(
    `${BASE}/conversations/integration/${integrationId}/phone/${enc(phoneE164)}/send`,
    { text },
    { headers: baseHeaders(), withCredentials: true },
  );
  return unwrap(response);
};

// POST /conversations/integration/{id}/phone/{enc}/send-template
// Always allowed (re-engagement). Body is { templateId, components? } —
// components is omitted when the template has no variables.
export const sendTemplate = async (integrationId, phoneE164, body) => {
  const response = await axios.post(
    `${BASE}/conversations/integration/${integrationId}/phone/${enc(phoneE164)}/send-template`,
    body,
    { headers: baseHeaders(), withCredentials: true },
  );
  return unwrap(response);
};
